import React, { ReactNode } from 'react';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { SecondaryButton } from 'src/shared/components/button/Button';
import CMSArticle from 'src/shared/components/cms-article/CMSArticle';
import Modal from 'src/shared/components/modal/Modal';
import styles from 'src/app/portfolio/views/portfolio-summary/components/SummaryCard.module.scss';

export type GenericSummaryCardProps = {
    cardIcon: ReactNode;
    cardTitle: ReactNode;
    cardSubtitle?: string;
    cardBody: ReactNode;
    titleExplainerSlug?: { defaultSlug: string; explainerSlug: string };
    action?: ReactNode;
    containerClasses?: string;
    frameworks?: string[];
    onExplainerHover?: (slug: string) => void;
    onDetailedHelpClick?: (slug: string) => void;
};

const SummaryCard = ({
    cardIcon,
    cardTitle,
    cardSubtitle,
    cardBody,
    titleExplainerSlug,
    action,
    frameworks,
    containerClasses = 'gap-1 p-4',
    onExplainerHover,
    onDetailedHelpClick,
}: GenericSummaryCardProps) => {
    const [selectedFramework, setSelectedFramework] = React.useState<string | null>(null);

    return (
        <>
            <div className={`flex-1 flex self-stretch flex-col bg-white rounded-xl shadow-portfolio-summary-card ${containerClasses}`}>
                <div className='flex place-content-between'>
                    <div className='flex gap-2 items-center'>
                        {cardIcon}
                        <div className='flex text-[17px] text-primary font-normal items-center'>
                            {cardTitle}
                            {titleExplainerSlug && (
                                <QuestionButtonHelp
                                    type='api'
                                    placement='right'
                                    slug={titleExplainerSlug.defaultSlug}
                                    detailedExplainerSlug={titleExplainerSlug.explainerSlug}
                                    onTooltipHover={() => onExplainerHover?.(titleExplainerSlug.defaultSlug)}
                                    onDetailedHelpClick={() => onDetailedHelpClick?.(titleExplainerSlug.explainerSlug)}
                                />
                            )}
                        </div>
                    </div>
                    {action}
                </div>
                {cardSubtitle && <div className='text-sm text-black font-normal mt-1 mb-2'>{cardSubtitle}</div>}
                {cardBody}
                {frameworks && frameworks.length > 0 && (
                    <div className='flex flex-col border-t border-grey-20 pt-[6px]'>
                        <div className='text-xs text-grey-80 mb-[6px]'>Reporting frameworks:</div>
                        <div className='flex gap-1'>
                            {frameworks.map(framework => (
                                <SecondaryButton
                                    key={framework}
                                    className='flex items-center rounded-lg text-sm h-8'
                                    onClick={() => {
                                        setSelectedFramework(framework);
                                    }}
                                >
                                    {framework}
                                </SecondaryButton>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {selectedFramework && (
                <Modal
                    onClose={() => {
                        setSelectedFramework(null);
                    }}
                    dismissOnClickOutside={true}
                    classNames={{
                        body: styles.reportingFrameworksModal,
                    }}
                >
                    <CMSArticle shouldShowTitle={true} slug={`portfolio-${selectedFramework.toLowerCase()}-framework`} />
                </Modal>
            )}
        </>
    );
};

export default SummaryCard;
